/**
 * Endpoints API service
 *
 * Provides functions for managing case infrastructure nodes (endpoints).
 * Supports IT systems, documents, data, and custom entity types.
 */

import { apiClient } from './api'
import type {
  CaseEndpoint,
  CreateEndpointRequest,
  CreateNodeRequest,
  UpdateEndpointRequest,
  UpdateNodeRequest,
  UpdatePositionRequest,
  ImportEndpointsRequest,
  EndpointImportResult,
  ListEndpointsQuery,
  LinkCollectionRequest,
  LinkEndpointRequest,
  CollectionStatusSummary,
} from '../types/case-container'

// =============================================================================
// HELPERS
// =============================================================================

/**
 * Build query string from params object
 */
function buildQueryString(params: object): string {
  const searchParams = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null) {
      searchParams.append(key, String(value))
    }
  }
  const queryString = searchParams.toString()
  return queryString ? `?${queryString}` : ''
}

// =============================================================================
// ENDPOINT API (Legacy + Extended)
// =============================================================================

/**
 * List all endpoints for a case
 */
export async function listEndpoints(
  caseId: string,
  params?: ListEndpointsQuery
): Promise<CaseEndpoint[]> {
  const query = params ? buildQueryString(params) : ''
  return apiClient.get<CaseEndpoint[]>(`/cases/${caseId}/endpoints${query}`)
}

/**
 * List endpoints with open questions
 */
export async function listWithOpenQuestions(caseId: string): Promise<CaseEndpoint[]> {
  return apiClient.get<CaseEndpoint[]>(`/cases/${caseId}/endpoints/open-questions`)
}

/**
 * List endpoints pending collection
 */
export async function listPendingCollection(caseId: string): Promise<CaseEndpoint[]> {
  return apiClient.get<CaseEndpoint[]>(`/cases/${caseId}/endpoints/pending-collection`)
}

/**
 * Get endpoint count for a case
 */
export async function countEndpoints(caseId: string): Promise<number> {
  return apiClient.get<number>(`/cases/${caseId}/endpoints/count`)
}

/**
 * Get collection status summary for a case
 */
export async function getCollectionStatusSummary(
  caseId: string
): Promise<CollectionStatusSummary[]> {
  return apiClient.get<CollectionStatusSummary[]>(
    `/cases/${caseId}/endpoints/collection-status-summary`
  )
}

/**
 * Get a single endpoint
 */
export async function getEndpoint(caseId: string, endpointId: string): Promise<CaseEndpoint> {
  return apiClient.get<CaseEndpoint>(`/cases/${caseId}/endpoints/${endpointId}`)
}

/**
 * Create a new infrastructure node
 */
export async function createNode(caseId: string, node: CreateNodeRequest): Promise<CaseEndpoint> {
  return apiClient.post<CaseEndpoint>(`/cases/${caseId}/infrastructure/nodes`, node)
}

/**
 * Create a new endpoint (legacy API)
 */
export async function createEndpoint(
  caseId: string,
  endpoint: CreateEndpointRequest
): Promise<CaseEndpoint> {
  return apiClient.post<CaseEndpoint>(`/cases/${caseId}/endpoints`, endpoint)
}

/**
 * Update an infrastructure node
 */
export async function updateNode(
  caseId: string,
  nodeId: string,
  updates: UpdateNodeRequest
): Promise<CaseEndpoint> {
  return apiClient.patch<CaseEndpoint>(`/cases/${caseId}/infrastructure/nodes/${nodeId}`, updates)
}

/**
 * Update an endpoint (legacy API)
 */
export async function updateEndpoint(
  caseId: string,
  endpointId: string,
  updates: UpdateEndpointRequest
): Promise<CaseEndpoint> {
  return apiClient.patch<CaseEndpoint>(`/cases/${caseId}/endpoints/${endpointId}`, updates)
}

/**
 * Update an endpoint's position (for LAN map drag)
 */
export async function updateEndpointPosition(
  caseId: string,
  endpointId: string,
  position: UpdatePositionRequest
): Promise<void> {
  await apiClient.patch(`/cases/${caseId}/endpoints/${endpointId}/position`, position)
}

/**
 * Link node to a collection
 */
export async function linkToCollection(
  caseId: string,
  nodeId: string,
  request: LinkCollectionRequest
): Promise<void> {
  await apiClient.post(`/cases/${caseId}/infrastructure/nodes/${nodeId}/link-collection`, request)
}

/**
 * Link infrastructure node to ingested endpoint
 */
export async function linkToEndpoint(
  caseId: string,
  nodeId: string,
  request: LinkEndpointRequest
): Promise<void> {
  await apiClient.post(`/cases/${caseId}/infrastructure/nodes/${nodeId}/link-endpoint`, request)
}

/**
 * Delete an endpoint
 */
export async function deleteEndpoint(caseId: string, endpointId: string): Promise<void> {
  await apiClient.delete(`/cases/${caseId}/endpoints/${endpointId}`)
}

/**
 * Import endpoints in bulk
 */
export async function importEndpoints(
  caseId: string,
  data: ImportEndpointsRequest
): Promise<EndpointImportResult> {
  const result = await apiClient.post<EndpointImportResult>(
    `/cases/${caseId}/endpoints/import`,
    data
  )
  return result
}

// =============================================================================
// INFRASTRUCTURE TEMPLATES
// =============================================================================

import type {
  TemplateFormat,
  TemplateDownloadQuery,
  ImportValidationResult,
  ImportExecuteRequest,
  ImportExecuteResult,
} from '../types/infrastructure'
import { tokenStorage } from './api'

import { API_BASE_URL } from './api-base-url'

function triggerFileDownload(blob: Blob, filename: string): void {
  const downloadUrl = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = downloadUrl
  link.download = filename
  document.body.append(link)
  link.click()
  link.remove()
  window.URL.revokeObjectURL(downloadUrl)
}

/**
 * Download infrastructure template as a file
 *
 * Triggers browser download of CSV or JSON template for infrastructure data import.
 * Templates include field definitions, validation info, and optionally example data.
 */
export async function downloadInfrastructureTemplate(
  caseId: string,
  format: TemplateFormat,
  options?: TemplateDownloadQuery
): Promise<void> {
  const query = buildQueryString({ includeExamples: options?.includeExamples })
  const url = `${API_BASE_URL}/cases/${caseId}/infrastructure/templates/${format}${query}`

  const headers: HeadersInit = {}
  const token = tokenStorage.getAccessToken()
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  const response = await fetch(url, {
    method: 'GET',
    headers,
  })

  if (!response.ok) {
    let errorBody: { error: string; message: string } | null = null
    try {
      errorBody = (await response.json()) as { error: string; message: string }
    } catch {
      // Body might not be JSON
    }
    throw new Error(errorBody?.message ?? response.statusText)
  }

  // Extract filename from Content-Disposition header
  const contentDisposition = response.headers.get('Content-Disposition')
  let filename = `infrastructure_template.${format}`
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename="([^"]+)"/)
    if (filenameMatch?.[1]) {
      filename = filenameMatch[1]
    }
  }

  triggerFileDownload(await response.blob(), filename)
}

// =============================================================================
// INFRASTRUCTURE IMPORT
// =============================================================================

/**
 * Validate an infrastructure import file
 *
 * Uploads a CSV or JSON file for validation without importing.
 * Returns validation results, errors, warnings, and duplicate information.
 */
export async function validateImportFile(
  caseId: string,
  file: File
): Promise<ImportValidationResult> {
  const formData = new FormData()
  formData.append('file', file)

  const headers: HeadersInit = {}
  const token = tokenStorage.getAccessToken()
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  const response = await fetch(`${API_BASE_URL}/cases/${caseId}/infrastructure/import/validate`, {
    method: 'POST',
    headers,
    body: formData,
  })

  if (!response.ok) {
    let errorBody: { error: string; message: string } | null = null
    try {
      errorBody = (await response.json()) as { error: string; message: string }
    } catch {
      // Body might not be JSON
    }
    throw new Error(errorBody?.message ?? response.statusText)
  }

  return response.json() as Promise<ImportValidationResult>
}

/**
 * Execute a validated infrastructure import
 *
 * Imports the validated records into the database.
 * Requires the records from a successful validation call.
 */
export async function executeImport(
  caseId: string,
  request: ImportExecuteRequest
): Promise<ImportExecuteResult> {
  return apiClient.post<ImportExecuteResult>(
    `/cases/${caseId}/infrastructure/import/execute`,
    request
  )
}
